import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { revertBooleanApiValue } from 'commons/utils/boolean'
import { verifyDiscount } from 'commons/utils/string'
import { getUser } from 'commons/utils/user'
import {
  Channels,
  type CustomBoolean,
  OriginOrder,
  OVTypes,
  TypesValidations,
} from 'constants/enums'
import { sapSuccessToast } from 'container/ManageOVDetails/utils'
import type {
  GenerateOV,
  Order,
  SalesOrderFieldsInterface,
  SimulateDraft,
} from 'container/SalesOrder/interfaces'
import { verifyIfIsClosedBox } from 'container/SalesOrder/utils'
import { toast } from 'react-toastify'
import {
  duplicateDraftOV,
  getDraftById,
  getListDraftItem,
  postMaintainDraft,
  postMaintainItem,
} from 'services/draft'
import type {
  QueryParamsListDraftItem,
  RequestBodyMaintainItem,
  RequestBodySimulateDraft,
  RequestParamsDuplicateDraft,
} from 'services/draft/interfaces'
import { cancelOvs, deleteOvs, getOVsByFilters, sendOvSap, setOvSap } from 'services/manage_ov'
import type {
  OVsByFilters,
  QueryParamsOVsByFilters,
  RequestOVsByFilters,
} from 'services/manage_ov/interface'
import { getSalesOrderOvs, rejectAnalysisRequest } from 'services/salesOrder'
import type { PaginationQuery, RejectAnalysisItem } from 'services/salesOrder/types'
import { asyncGetOVById } from 'store/OVContext'
import type {
  CancelOvsBody,
  RequestOvSap,
  RequestOvSapBody,
  SapTypeToOvStatus,
  SendOvsBody,
} from 'store/OVContext/ovContextTypes'
import { setDefaultParams } from 'store/ProductContext'
import type { AppState, AppThunk } from 'store/store'

import { defaultRequestOvValues, type InfosOvInterface } from './types/InfosOV'
import type { MaterialsListInterface } from './types/MaterialItemData'
import type { SalesOrderOvResponse } from './types/ovsList'
import type { RequestBodyMaintainDraft } from './types/RequestBodyMaintainDraft'
import type { SalesOrderData } from './types/SalesOrderData'

type MatrizProp = {
  cd_cor: string
  cd_tamh: string
  qt_item: number
  fl_sele?: CustomBoolean
  fl_excl_item: CustomBoolean
}

type ListItemsDraftProps = {
  cd_matr: string
  nr_item_pedi_rasc: number
  pc_dsct: number
  cd_cent_matr: string
  qt_caix_grad: number
  fl_caix_grad: CustomBoolean
  matriz: MatrizProp[]
}

type InitialState = {
  loadingMaterials: boolean
  secureRecords: MaterialsListInterface[]
  emptyData: boolean
  hasFiltering: boolean
  records: MaterialsListInterface[]
  analysisRecords: MaterialsListInterface[]
  pendingRecords: MaterialsListInterface[]
  rejectedRecords: MaterialsListInterface[]
  initialRecords: unknown[]
  discount: string
  duplicateChecked: boolean[]
  numberDraft: string
  materialsLimitExceed: boolean
  draftBody: SalesOrderData
  duplicateOrder: RequestParamsDuplicateDraft
  simulationAlteredMaterials: string[]
  checkedRecords: Array<unknown>
  ovsByFilters: OVsByFilters[]
  isLoadingOVs: boolean
  isFilteringOVs: boolean
  checkedOvs: OVsByFilters[]
  orderSuccess: GenerateOV
  selectedOvNegDif: Order[]
  simulateOrder: SimulateDraft | null
  persistFormData: SalesOrderFieldsInterface
  proceedDisabled: boolean
  materialNoAvailable: boolean
  shouldSendToAnalysis: boolean
  ovsListData: SalesOrderOvResponse
  isLoadingOvsList: boolean
  materialSearch: string
  requestSimulate: RequestBodySimulateDraft
  isInternationalOrder: boolean
  isYTRA: boolean
  isYPOP: boolean
  isSampleOrder: boolean
  isYASC: boolean
  isHAVAConectaOrder: boolean
  isND: boolean
  isInternationalEdi: boolean
  isEDI: boolean
  loadingItemUpdate: boolean
  isYRBA: boolean
}

export const salesOrderInitialState: InitialState = {
  analysisRecords: [],
  checkedOvs: [],
  checkedRecords: [],
  discount: '0',
  draftBody: {} as SalesOrderData,
  duplicateChecked: [true, false],
  duplicateOrder: {} as RequestParamsDuplicateDraft,
  emptyData: true,
  hasFiltering: false,
  initialRecords: [],
  isEDI: false,
  isFilteringOVs: false,
  isHAVAConectaOrder: false,
  isInternationalEdi: false,
  isInternationalOrder: false,
  isLoadingOVs: false,
  isLoadingOvsList: true,
  isND: false,
  isSampleOrder: false,
  isYASC: false,
  isYPOP: false,
  isYRBA: false,
  isYTRA: false,
  loadingItemUpdate: false,
  loadingMaterials: false,
  materialNoAvailable: false,
  materialSearch: '',
  materialsLimitExceed: false,
  numberDraft: '',
  orderSuccess: {} as GenerateOV,
  ovsByFilters: [],
  ovsListData: {
    OrdensVenda: [],
    RowsCount: 0,
    ValorTotalOrdensVenda: 0,
  } as SalesOrderOvResponse,
  pendingRecords: [],
  persistFormData: {} as SalesOrderFieldsInterface,
  proceedDisabled: false,
  records: [],
  rejectedRecords: [],
  requestSimulate: {} as RequestBodySimulateDraft,
  secureRecords: [],
  selectedOvNegDif: [],
  shouldSendToAnalysis: true, //TODO: remover propriedade após virada do estoque
  simulateOrder: null,
  simulationAlteredMaterials: [],
}

export const salesOrderSlice = createSlice({
  initialState: salesOrderInitialState,
  name: 'salesOrder',
  reducers: {
    addSimulationAlteredMaterial(state, action: PayloadAction<string>) {
      if (!state.simulationAlteredMaterials.includes(action.payload)) {
        state.simulationAlteredMaterials = [...state.simulationAlteredMaterials, action.payload]
      }
    },
    clearSimulationAlteredMaterials(state) {
      state.simulationAlteredMaterials = []
    },
    removeSimulationAlteredMaterial(state, action: PayloadAction<string>) {
      if (state.simulationAlteredMaterials.includes(action.payload)) {
        state.simulationAlteredMaterials = state.simulationAlteredMaterials.filter(
          (element) => element !== action.payload,
        )
      }
    },
    resetSalesOrder(state) {
      state.secureRecords = []
      state.emptyData = true
      state.hasFiltering = false
      state.records = []
      state.analysisRecords = []
      state.pendingRecords = []
      state.rejectedRecords = []
      state.initialRecords = []
      state.discount = '0'
      state.duplicateChecked = [true, false]
      state.numberDraft = ''
      state.materialsLimitExceed = false
      state.draftBody = {} as SalesOrderData
      state.duplicateOrder = {} as RequestParamsDuplicateDraft
      state.simulationAlteredMaterials = []
      state.checkedRecords = []
      state.ovsByFilters = []
      state.isLoadingOVs = false
      state.isFilteringOVs = false
      state.checkedOvs = []
      state.orderSuccess = {} as GenerateOV
      state.selectedOvNegDif = []
      state.simulateOrder = null
      state.persistFormData = {} as SalesOrderFieldsInterface
      state.proceedDisabled = false
      state.materialNoAvailable = false
      state.isInternationalOrder = false
      state.isYTRA = false
      state.isYPOP = false
      state.isSampleOrder = false
      state.isYASC = false
      state.isHAVAConectaOrder = false
      state.isND = false
      state.isEDI = false
      state.isYRBA = false
    },
    setAnalysisRecords: (state, action: PayloadAction<MaterialsListInterface[]>) => {
      state.analysisRecords = action.payload
    },
    setCheckedOvs(state, action: PayloadAction<OVsByFilters[]>) {
      state.checkedOvs = action.payload
    },
    setCheckedRecords(state, action: PayloadAction<Array<unknown>>) {
      if (typeof action.payload === 'object') {
        state.checkedRecords = action.payload
      }
    },
    setDiscount: (state, action: PayloadAction<string>) => {
      state.discount = action.payload
    },
    setDraftBody(state, action: PayloadAction<SalesOrderData>) {
      if (typeof action.payload === 'object') {
        state.draftBody = action.payload
      }
    },
    setDuplicateChecked: (state, action: PayloadAction<boolean[]>) => {
      state.duplicateChecked = action.payload
    },
    setDuplicateOrder(state, action: PayloadAction<RequestParamsDuplicateDraft>) {
      if (typeof action.payload === 'object') {
        state.duplicateOrder = action.payload
      }
    },
    setEmptyData: (state, action: PayloadAction<boolean>) => {
      state.emptyData = action.payload
    },
    setHasFiltering: (state, action: PayloadAction<boolean>) => {
      state.hasFiltering = action.payload
    },
    setInitialRecords: (state, action: PayloadAction<unknown[]>) => {
      state.initialRecords = action.payload
    },
    setIsEDI(state, action: PayloadAction<boolean>) {
      state.isEDI = action.payload
    },
    setIsFilteringOVs(state, action: PayloadAction<boolean>) {
      state.isFilteringOVs = action.payload
    },
    setIsHAVAConectaOrder(state, action: PayloadAction<boolean>) {
      state.isHAVAConectaOrder = action.payload
    },
    setIsInternationalEdi(state, action: PayloadAction<boolean>) {
      state.isInternationalEdi = action.payload
    },
    setIsInternationalOrder(state, action: PayloadAction<boolean>) {
      state.isInternationalOrder = action.payload
    },
    setIsLoadingOVs: (state, action: PayloadAction<boolean>) => {
      state.isLoadingOVs = action.payload
    },
    setIsLoadingOVsList(state, action: PayloadAction<boolean>) {
      state.isLoadingOvsList = action.payload
    },
    setIsND(state, action: PayloadAction<boolean>) {
      state.isND = action.payload
    },
    setIsSampleOrder(state, action: PayloadAction<boolean>) {
      state.isSampleOrder = action.payload
    },
    setIsYASC(state, action: PayloadAction<boolean>) {
      state.isYASC = action.payload
    },
    setIsYPOP(state, action: PayloadAction<boolean>) {
      state.isYPOP = action.payload
    },
    setIsYRBA(state, action: PayloadAction<boolean>) {
      state.isYRBA = action.payload
    },
    setIsYTRA(state, action: PayloadAction<boolean>) {
      state.isYTRA = action.payload
    },
    setLoadingItemUpdate(state, action: PayloadAction<boolean>) {
      state.loadingItemUpdate = action.payload
    },
    setLoadingMaterials: (state, action: PayloadAction<boolean>) => {
      state.loadingMaterials = action.payload
    },
    setMaterialNoAvailable(state, action: PayloadAction<boolean>) {
      state.materialNoAvailable = action.payload
    },
    setMaterialSearch: (state, action: PayloadAction<string>) => {
      state.materialSearch = action.payload
    },
    setMaterialsLimitExceed: (state, action: PayloadAction<boolean>) => {
      state.materialsLimitExceed = action.payload
    },
    setNumberDraft: (state, action: PayloadAction<string>) => {
      state.numberDraft = action.payload
    },
    setOrderSuccess(state, action: PayloadAction<GenerateOV>) {
      state.orderSuccess = action.payload
    },
    setOVsByFilters(state, action: PayloadAction<OVsByFilters[]>) {
      if (typeof action.payload === 'object') {
        state.ovsByFilters = action.payload
      }
    },
    setOvsList(state, action: PayloadAction<SalesOrderOvResponse>) {
      state.ovsListData = action.payload
    },
    setPendingRecords(state, action: PayloadAction<MaterialsListInterface[]>) {
      state.pendingRecords = action.payload
    },
    setPersistFormData(state, action: PayloadAction<SalesOrderFieldsInterface>) {
      state.persistFormData = action.payload
    },
    setProceedDisabled(state, action: PayloadAction<boolean>) {
      state.proceedDisabled = action.payload
    },
    setRecords: (state, action: PayloadAction<MaterialsListInterface[]>) => {
      state.records = action.payload
    },
    setRejectedRecords: (state, action: PayloadAction<MaterialsListInterface[]>) => {
      state.rejectedRecords = action.payload
    },
    setRequestSimulate(state, action: PayloadAction<RequestBodySimulateDraft>) {
      state.requestSimulate = action.payload
    },
    setSecure: (state, action: PayloadAction<MaterialsListInterface[]>) => {
      state.secureRecords = action.payload
      state.emptyData = state.records.length === 0
    },
    setSelectedOvNegDif(state, action: PayloadAction<Order[]>) {
      state.selectedOvNegDif = action.payload
    },
    setShouldSendToAnalysis(state, action: PayloadAction<boolean>) {
      state.shouldSendToAnalysis = action.payload
    },
    setSimulateOrder(state, action: PayloadAction<SimulateDraft | null>) {
      state.simulateOrder = action.payload
    },
    updateRecords: (state, action: PayloadAction<MaterialsListInterface[]>) => {
      state.initialRecords = action.payload
      state.records = action.payload
      state.secureRecords = action.payload
      state.emptyData = state.records.length === 0
      state.pendingRecords = action.payload.filter(
        (record) => record.cd_stat_pedi_rasc_item === 0 || record.cd_stat_pedi_rasc_item === 20,
      )
    },
  },
})

export const {
  setLoadingMaterials,
  setDiscount,
  setDuplicateChecked,
  setEmptyData,
  setHasFiltering,
  setIsLoadingOVs,
  setInitialRecords,
  setMaterialsLimitExceed,
  setNumberDraft,
  setRecords,
  setAnalysisRecords,
  setRejectedRecords,
  setSecure,
  setPendingRecords,
  updateRecords,
  setDraftBody,
  setDuplicateOrder,
  addSimulationAlteredMaterial,
  removeSimulationAlteredMaterial,
  clearSimulationAlteredMaterials,
  setCheckedRecords,
  setOVsByFilters,
  setIsFilteringOVs,
  setCheckedOvs,
  setOrderSuccess,
  setSelectedOvNegDif,
  setSimulateOrder,
  setPersistFormData,
  setProceedDisabled,
  setMaterialNoAvailable,
  setShouldSendToAnalysis,
  resetSalesOrder,
  setOvsList,
  setIsLoadingOVsList,
  setMaterialSearch,
  setRequestSimulate,
  setIsInternationalOrder,
  setIsYTRA,
  setIsYPOP,
  setIsYASC,
  setIsSampleOrder,
  setIsHAVAConectaOrder,
  setIsND,
  setIsInternationalEdi,
  setIsEDI,
  setLoadingItemUpdate,
  setIsYRBA,
} = salesOrderSlice.actions

export default salesOrderSlice.reducer
export const states = (state: AppState) => state.salesOrder

const bodyParamsListItens: QueryParamsListDraftItem = {
  nr_pagi: 0,
  nr_pedi_rasc: '',
  qt_rowcount: 999,
}

export const getItems =
  (pedido?: string, seachValue?: string): AppThunk =>
  async (dispatch, getState) => {
    const { numberDraft: nr_pedi_rasc } = states(getState())
    dispatch(setLoadingMaterials(true))
    const orderNumber = pedido ? pedido : nr_pedi_rasc
    getListDraftItem({
      ...bodyParamsListItens,
      matr: seachValue ? seachValue : '',
      nr_pedi_rasc: orderNumber,
    })
      .then((data) => {
        dispatch(updateRecords(data))
      })
      .finally(() => {
        dispatch(fetchOrderStatistics(orderNumber))
        dispatch(setLoadingMaterials(false))
      })
  }

export const getAnalysisItems =
  (seachValue?: string, factoryFilters?: string[], statusFilters?: string[]): AppThunk =>
  async (dispatch, getState) => {
    const { numberDraft: nr_pedi_rasc } = states(getState())
    dispatch(setLoadingMaterials(true))
    getListDraftItem({
      ...bodyParamsListItens,
      matr: seachValue ? seachValue : '',
      nr_pedi_rasc,
    })
      .then((data) => {
        const analysisData = data.filter((item) => {
          const factoryMatch = !factoryFilters?.length || factoryFilters.includes(item.cd_cent_matr)
          const statusMatch = !statusFilters?.length || statusFilters.includes(item.ds_moti_crit)
          return item.cd_stat_pedi_rasc_item === 10 && factoryMatch && statusMatch
        })
        dispatch(setAnalysisRecords(analysisData))
      })
      .finally(() => {
        dispatch(setLoadingMaterials(false))
      })
  }

export const getRejectedItems =
  (seachValue?: string, factoryFilters?: string[], statusFilters?: string[]): AppThunk =>
  async (dispatch, getState) => {
    const { numberDraft: nr_pedi_rasc } = states(getState())
    dispatch(setLoadingMaterials(true))
    getListDraftItem({
      ...bodyParamsListItens,
      matr: seachValue ? seachValue : '',
      nr_pedi_rasc,
    })
      .then((data) => {
        const rejectedData = data.filter((item) => {
          const factoryMatch = !factoryFilters?.length || factoryFilters.includes(item.cd_cent_matr)
          const statusMatch = !statusFilters?.length || statusFilters.includes(item.ds_moti_crit)
          return item.cd_stat_pedi_rasc_item === 90 && factoryMatch && statusMatch
        })
        dispatch(setRejectedRecords(rejectedData))
      })
      .finally(() => {
        dispatch(setLoadingMaterials(false))
      })
  }

export const postDraft =
  (
    bodyParams: RequestBodyMaintainDraft,
    callback?: (code?: string[]) => void,
    upItems?: boolean,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingItemUpdate(true))
    try {
      const saveDraft = await postMaintainDraft(bodyParams)
      if (upItems && saveDraft) {
        if (callback) callback()
        return
      }

      dispatch(getItems(''))
    } catch (error) {
      const { message } = error as Record<string, string>
      const codes = message.match(/(\d{7})/gi)

      if (callback && codes) callback(codes)
      toast.error('Erro ao salvar rascunho')
    } finally {
      dispatch(setLoadingItemUpdate(false))
    }
  }
export const postItens =
  (bodyParams: RequestBodyMaintainItem, callback?: (code?: string[]) => void): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingItemUpdate(true))
    try {
      const saveDraft = await postMaintainItem(bodyParams)
      if (saveDraft) {
        if (callback) callback()
        return
      }

      dispatch(getItems(''))
    } catch (error) {
      const { message } = error as Record<string, string>
      const codes = message.match(/(\d{7})/gi)

      if (callback && codes) callback(codes)
      toast.error('Erro ao atualizar item')
    } finally {
      dispatch(setLoadingItemUpdate(false))
    }
  }

export const getQtdByOrderType = (
  t: {
    cd_tamh: string
    id_tabl_prec: number
    qt_item: number
    qt_item_conv: number
    vl_dsct_unit: number
    vl_prec_unit: number
  },
  flagMixedBox: CustomBoolean,
) => {
  if (/\D/g.test(t.cd_tamh)) return t.qt_item
  if (flagMixedBox === TypesValidations.YES) return t.qt_item
  return t.qt_item_conv
}

export const updateItem =
  (material: MaterialsListInterface, callback: (code?: string[]) => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingItemUpdate(true))
    const { numberDraft: nr_pedi_rasc } = states(getState())

    const matriz = material.tamanhos.map((t) => ({
      cd_cor: material.cd_cor,
      cd_tamh: t.cd_tamh,
      fl_excl_item: material.fl_excl_item,
      fl_sele: material.fl_sele,
      qt_item: getQtdByOrderType(t, material.fl_caix_grad),
    }))

    const bodyParamsMaintainRascunho: RequestBodyMaintainItem = {
      cd_cent_matr: material.cd_cent_matr,
      cd_matr: material.cd_matr,
      cd_usua_alte: getUser(),
      fl_caix_grad: material.fl_caix_grad,
      fl_conv: verifyIfIsClosedBox(material) ? TypesValidations.NO : TypesValidations.YES,
      matriz,
      nr_item_pedi_rasc: material.nr_item_pedi_rasc,
      nr_pedi_rasc,
      pc_dsct: verifyDiscount(material.pc_dsct.toString()),
      qt_caix_grad: material.qt_caix_grad,
    }
    dispatch(postItens(bodyParamsMaintainRascunho, callback))
  }

export const updateAllItems =
  (callback: (code?: string[]) => void, state?: CustomBoolean, upItems?: boolean): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingItemUpdate(true))
    const { records, numberDraft: nr_pedi_rasc } = states(getState())

    async function RequestRascunhoById() {
      const returnApi = await getDraftById({ nr_pedi_rasc })
      const isYPOP = returnApi?.cd_tipo_orde === OVTypes.YPOP
      const isYASC = returnApi?.cd_tipo_orde === OVTypes.YASC
      const isYRBA = returnApi?.cd_tipo_orde === OVTypes.YRBA
      const isEDI = returnApi?.tp_orig === OriginOrder.EDI

      dispatch(setIsHAVAConectaOrder(returnApi?.tp_orig === OriginOrder.HAVA))
      dispatch(setIsYTRA(returnApi?.cd_tipo_orde === OVTypes.YTRA))
      dispatch(setIsYPOP(isYPOP))
      dispatch(setIsYASC(isYASC))
      dispatch(setIsYRBA(isYRBA))
      dispatch(setIsSampleOrder(isYPOP || isYASC))
      dispatch(setIsND(returnApi?.fl_negc_difd === TypesValidations.YES))
      dispatch(setIsInternationalEdi(returnApi?.fl_edi_intl || false))
      dispatch(setIsEDI(isEDI))
      updateDraft(returnApi)
    }

    RequestRascunhoById()

    function updateDraft(draft: InfosOvInterface | void) {
      const listItemsDraft: Array<ListItemsDraftProps> = []
      records
        .filter(
          (record) => record.cd_stat_pedi_rasc_item === 0 || record.cd_stat_pedi_rasc_item === 20,
        )
        .forEach((material: MaterialsListInterface) => {
          const matriz = material.tamanhos.map((t) => ({
            cd_cor: material.cd_cor,
            cd_tamh: t.cd_tamh,
            fl_excl_item: material.fl_excl_item,
            fl_sele: state,
            qt_item: getQtdByOrderType(t, material.fl_caix_grad),
          }))

          listItemsDraft.push({
            cd_cent_matr: material.cd_cent_matr,
            cd_matr: material.cd_matr,
            fl_caix_grad: material.fl_caix_grad,
            matriz,
            nr_item_pedi_rasc: material.nr_item_pedi_rasc,
            pc_dsct: verifyDiscount(material.pc_dsct.toString()),
            qt_caix_grad: material.qt_caix_grad,
          })
        })
      const bodyParamsMaintainRascunho: RequestBodyMaintainDraft = {
        ...draft,
        cd_clie_entr: draft?.cd_clie_entr,
        ds_item_pedi_rasc: listItemsDraft,
        nr_pedi_cpra: '',
      }
      dispatch(postDraft(bodyParamsMaintainRascunho, callback, upItems))
    }
  }

export function asyncGetCorpoRascunho(pedido: string, successCallback?: () => void): AppThunk {
  return async function (dispatch) {
    const data = await getDraftById({ nr_pedi_rasc: pedido })
    if (typeof data === 'object') {
      const isYPOP = data.cd_tipo_orde === OVTypes.YPOP
      const isYASC = data.cd_tipo_orde === OVTypes.YASC
      const isYRBA = data.cd_tipo_orde === OVTypes.YRBA
      const isEDI = data.tp_orig === OriginOrder.EDI

      dispatch(setIsHAVAConectaOrder(data.tp_orig === OriginOrder.HAVA))
      dispatch(setIsYTRA(data.cd_tipo_orde === OVTypes.YTRA))
      dispatch(setIsYPOP(isYPOP))
      dispatch(setIsYASC(isYASC))
      dispatch(setIsYRBA(isYRBA))
      dispatch(setIsSampleOrder(isYPOP || isYASC))
      dispatch(setIsND(data.fl_negc_difd === TypesValidations.YES))
      dispatch(setDraftBody(data))
      dispatch(setIsInternationalOrder(data.cd_canl_dstb === Channels.ME))
      dispatch(setIsInternationalEdi(data.fl_edi_intl))
      dispatch(setIsEDI(isEDI))

      dispatch(
        setDefaultParams({
          cd_canl_dstb: data.cd_canl_dstb,
          cd_empr: data.cd_empr,
          cd_equi_vend: data.cd_equi_vend,
          cd_escr_vend: data.cd_escr_vend,
          cd_orgn_vend: data.cd_orgn_vend,
          cd_regi_vend: data.cd_regi_vend,
          cd_seto_atvd: data.cd_seto_atvd,
          cd_tipo_orde: data.cd_tipo_orde,
          fl_matr_fora_linh: data.fl_matr_fora_linh,
          fl_mto_prep: data.fl_mto_prep as CustomBoolean,
          nr_pedi_rasc: pedido,
        }),
      )
      successCallback && successCallback()
    }
  }
}

export function asyncGetOVsByFilters(
  query: QueryParamsOVsByFilters,
  body: RequestOVsByFilters,
): AppThunk {
  return async function (dispatch) {
    try {
      dispatch(setIsLoadingOVs(true))
      const ovsData = await getOVsByFilters(query, body)
      const ovsByFiltersData = ovsData.length
        ? ovsData.map((ov) => ({ ...ov, checked: false }))
        : []
      dispatch(setOVsByFilters(ovsByFiltersData))
      dispatch(setCheckedOvs([]))
    } catch (error) {
      toast.error('Erro obter Ovs por filtro')
    } finally {
      dispatch(setIsLoadingOVs(false))
    }
  }
}

export const asyncSetOvSap =
  ({ type, body, currentPage = 1, pageSize = 10, orderNumber }: RequestOvSap): AppThunk =>
  async (dispatch) => {
    try {
      const requestType = type === 'devolver' ? 'retornar' : type

      const sendBody = body?.map((ov) => ({
        DataAlteracao: ov.dataAlteracao,
        NumeroOrdemVenda: ov.numeroOrdemVenda,
      }))

      const response =
        type === 'enviar' && sendBody !== undefined
          ? await sendOvSap({ ListaOvs: sendBody })
          : await setOvSap({ body, type: requestType })
      const ovSapData = response.data
      const checkError = ovSapData.findIndex((ovSap) => !ovSap.OperacaoRealizada)
      const checkSuccess = ovSapData.findIndex((ovSap) => ovSap.OperacaoRealizada)
      dispatch(setIsLoadingOVs(true))
      if (checkSuccess > -1) {
        if (orderNumber) {
          dispatch(
            asyncGetOVsByFilters(
              { currentPage: currentPage - 1, pageSize: pageSize },
              { ...defaultRequestOvValues, numeroPedidoRascunho: [orderNumber] },
            ),
          )
        }
        dispatch(setCheckedOvs([]))
        if (checkError > -1) {
          toast.warning('Não foi possivel realizar a operação em algumas das Ovs selecionadas.')
        } else {
          sapSuccessToast(type)
        }
      } else {
        toast.error(ovSapData[checkError].MensagemRetorno)
      }
    } catch (error) {
      toast.error(`Erro ao ${type}`)
    } finally {
      dispatch(setIsLoadingOVs(false))
    }
  }

export const asyncGetSalesOrderOvs =
  (query: PaginationQuery, orderNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoadingOVsList(true))
    try {
      const ovsResponse = await getSalesOrderOvs(query, orderNumber)
      const ovsData = ovsResponse.RowsCount
        ? {
            ...ovsResponse,
            OrdensVenda: ovsResponse.OrdensVenda.map((ov) => ({
              ...ov,
              checked: false,
              StatusDescription: ov.StatusOrdemVenda.split('-')[1].trim(),
              StatusNumber: ov.StatusOrdemVenda.split('-')[0].trim(),
            })),
          }
        : { OrdensVenda: [], RowsCount: 0, ValorTotalOrdensVenda: 0 }
      dispatch(setOvsList(ovsData))
    } catch (error) {
      toast.error('Erro obter ordens de venda do pedido.')
    } finally {
      dispatch(setIsLoadingOVsList(false))
    }
  }

export const SalesOrderSapOperation =
  (
    operation: string,
    body: RequestOvSapBody,
    query: PaginationQuery,
    orderNumber: string,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoadingOVsList(true))
    const type = operation as SapTypeToOvStatus
    try {
      const response = await setOvSap({ body, type })
      const ovSapData = response.data
      const checkError = ovSapData.findIndex((ovSap) => !ovSap.OperacaoRealizada)
      const checkSuccess = ovSapData.findIndex((ovSap) => ovSap.OperacaoRealizada)

      if (checkSuccess > -1) {
        if (checkError > -1) {
          toast.warning('Não foi possivel realizar a operação em algumas das OVs selecionadas.')
        } else {
          toast.success('Operação realizada com sucesso.')
        }
      } else {
        toast.error(ovSapData[checkError].MensagemRetorno)
      }
    } catch (error) {
      toast.error('Erro ao realizar operação.')
    } finally {
      dispatch(asyncGetSalesOrderOvs(query, orderNumber))
      dispatch(fetchOrderStatistics(orderNumber))
    }
  }

export const SendSapOperation =
  (body: SendOvsBody, query: PaginationQuery, orderNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoadingOVsList(true))
    try {
      const response = await sendOvSap(body)
      const ovSapData = response.data
      const checkError = ovSapData.findIndex((ovSap) => !ovSap.OperacaoRealizada)
      const checkSuccess = ovSapData.findIndex((ovSap) => ovSap.OperacaoRealizada)

      if (checkSuccess > -1) {
        if (checkError > -1) {
          toast.warning('Não foi possivel realizar a operação em algumas das OVs selecionadas.')
        } else {
          toast.success('Operação realizada com sucesso.')
        }
      } else {
        toast.error(ovSapData[checkError].MensagemRetorno)
      }
    } catch (error) {
      toast.error('Erro ao realizar operação.')
    } finally {
      dispatch(asyncGetSalesOrderOvs(query, orderNumber))
      dispatch(fetchOrderStatistics(orderNumber))
    }
  }

export const SalesOrderOvDeleteOperation =
  (body: string[], query: PaginationQuery, orderNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoadingOVsList(true))
    try {
      const result = await deleteOvs(body)
      if (result.OperacaoRealizada)
        toast.success(
          body?.length === 1 ? 'OV Excluída com sucesso' : 'Operação realizada com sucesso.',
        )
    } catch (error) {
      toast.error(
        'Não foi possível excluir a OV devido a um erro. Tente novamente mais tarde ou entre em contato com o suporte técnico.',
      )
    } finally {
      if (orderNumber) {
        dispatch(asyncGetSalesOrderOvs(query, orderNumber))
        dispatch(fetchOrderStatistics(orderNumber))
      }
    }
  }

export const SalesOrderOvCancelOperation =
  (body: CancelOvsBody, query: PaginationQuery, orderNumber: string): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoadingOVsList(true))
    try {
      const result = await cancelOvs(body)
      if (result.OperacaoRealizada)
        toast.success(
          body?.ordensVenda?.length === 1
            ? 'OV cancelada com sucesso'
            : 'Operação realizada com sucesso.',
        )
    } catch (error) {
      toast.error(
        'Não foi possível cancelar a OV devido a um erro. Tente novamente mais tarde ou entre em contato com o suporte técnico.',
      )
    } finally {
      if (body?.ordensVenda?.length === 1) {
        dispatch(asyncGetOVById(body?.ordensVenda[0].numeroOrdem))
      }
      if (orderNumber) {
        dispatch(asyncGetSalesOrderOvs(query, orderNumber))
        dispatch(fetchOrderStatistics(orderNumber))
      }
    }
  }

export function fetchOrderStatistics(order: string): AppThunk {
  return async function (dispatch, getState) {
    const data = await getDraftById({ nr_pedi_rasc: order })
    const { draftBody } = getState().salesOrder
    if (typeof data === 'object') {
      const isYPOP = data.cd_tipo_orde === OVTypes.YPOP
      const isYASC = data.cd_tipo_orde === OVTypes.YASC
      const isYRBA = data.cd_tipo_orde === OVTypes.YRBA
      const isEDI = data.tp_orig === OriginOrder.EDI

      dispatch(setIsHAVAConectaOrder(data.tp_orig === OriginOrder.HAVA))
      dispatch(setIsYTRA(data.cd_tipo_orde === OVTypes.YTRA))
      dispatch(setIsYPOP(isYPOP))
      dispatch(setIsYASC(isYASC))
      dispatch(setIsYRBA(isYRBA))
      dispatch(setIsSampleOrder(isYPOP || isYASC))
      dispatch(setDraftBody({ ...draftBody, totalizador: data.totalizador }))
      dispatch(setIsND(data.fl_negc_difd === TypesValidations.YES))
      dispatch(setIsInternationalOrder(data.cd_canl_dstb === Channels.ME))
      dispatch(setIsInternationalEdi(data.fl_edi_intl))
      dispatch(setIsEDI(isEDI))
    }
  }
}

export const interruptAnalysis =
  (items: RejectAnalysisItem[]): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingMaterials(true))
    try {
      const body = {
        cd_moti_repv_anls: '99',
        itens: items,
        restrito: revertBooleanApiValue(false),
      }
      const response = await rejectAnalysisRequest(body)
      if (response) {
        dispatch(getItems())
        dispatch(getAnalysisItems())
        toast.success('Análise interrompida com sucesso.')
      }
    } catch {
      toast.error('Erro ao interromper análise.')
      dispatch(setLoadingMaterials(false))
    }
  }

export const replicateRecords =
  (items: MaterialsListInterface[], successCallback: (order: string) => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoadingMaterials(true))
    const { draftBody } = getState().salesOrder
    try {
      const uniqueItems = new Set<string>(items.map((item) => `${item.nr_item_pedi_rasc}`))
      const material = [...uniqueItems].map((item) => {
        const colors = items
          .filter((record) => record.nr_item_pedi_rasc === Number(item))
          .map((record) => record.cd_cor)
        return {
          cd_cor: colors,
          nr_item: item,
        }
      })
      const cliente = {
        cd_clie: draftBody.cd_clie,
        fl_dupl_matr: revertBooleanApiValue(true),
        fl_dupl_text: revertBooleanApiValue(true),
        fl_mto_prep: draftBody.fl_mto_prep,
        material,
        nr_pedi_clie: draftBody.nr_pedi_clie,
      }
      const body = {
        Clientes: [cliente],
        cd_usua_alte: getUser(),
        nr_pedi_rasc_orig: draftBody.nr_pedi_rasc,
      }
      const response = await duplicateDraftOV(body)
      if (response[0].OperacaoRealizada) {
        successCallback(response[0].NumeroPedido)
        toast.success('Materiais reutilizados com sucesso.')
      } else {
        toast.error(response[0].MensagemRetorno)
      }
    } catch {
      toast.error('Erro ao reutilizar materiais.')
    } finally {
      dispatch(setLoadingMaterials(false))
    }
  }
